import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { createProposal, type Frise as FriseData, fetchProposals, queryKeys } from "./api";
import { AppPage } from "./app-nav";
import { whenLabel } from "./dates";
import { DeleteAccount } from "./delete-account";
import { EntrySheet } from "./entry-form";
import { Frise } from "./frise";
import { datedInOrder, undatedEntries } from "./frise-layout";
import { UndatedTray } from "./undated-tray";

/**
 * Ce que voit un proche (spec, section 1, ecran 2 ; planche « VueProche » ; ticket 7) : la meme
 * frise en lecture seule, reduite par le serveur aux points qui lui sont ouverts, ses propositions
 * en attente, et « Proposer un souvenir ». Rien ne se filtre ici : le serveur n'a rien envoye
 * d'autre.
 */
export function MemberFrisePage({ frise, today }: { frise: FriseData; today: string }) {
  const queryClient = useQueryClient();
  const proposals = useQuery({
    queryKey: queryKeys.proposals,
    queryFn: fetchProposals,
    retry: false,
  });
  const [proposing, setProposing] = useState(false);
  const dated = datedInOrder(frise.entries);
  const undated = undatedEntries(frise.entries);
  const mine = proposals.data?.proposals ?? [];
  const tracks = frise.tracks;

  return (
    <AppPage viewerRole={frise.role}>
      <div className="flex flex-col gap-2">
        <h1 className="tl-display m-0 text-[30px] md:text-[44px]">
          {frise.ownerName === null ? "Sa vie, " : `La vie de ${frise.ownerName}, `}
          <em className="text-tl-accent italic">en étoiles</em>
        </h1>
        <p className="m-0 text-sm text-muted-foreground">
          Tu vois les moments qu'on t'a ouverts. Tu te souviens d'autre chose ? Propose-le,{" "}
          {frise.ownerName ?? "la personne dont c'est la frise"} décidera.
        </p>
      </div>

      {dated.length > 0 ? (
        <Frise birthDate={null} entries={frise.entries} today={today} tracks={tracks} />
      ) : (
        <div className="flex flex-col items-start gap-2 rounded-xl border border-border bg-card p-5">
          <p className="tl-display m-0 text-2xl">Rien ne t'est encore ouvert ici.</p>
          <p className="m-0 text-sm text-muted-foreground">
            Tu peux quand même proposer un souvenir.
          </p>
        </div>
      )}

      <UndatedTray entries={undated} tracks={tracks} />

      {mine.length > 0 ? (
        <section aria-labelledby="mes-propositions" className="flex flex-col gap-2">
          <h2 className="m-0 text-[15px] font-semibold" id="mes-propositions">
            {mine.length === 1 ? "Ta proposition, en attente" : "Tes propositions, en attente"}
          </h2>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {mine.map((proposal) => (
              <li
                className="flex flex-col gap-0.5 rounded-[14px] border border-[rgba(243,238,227,0.16)] bg-card px-3.5 py-3"
                key={proposal.id}
              >
                <strong className="text-sm">{proposal.title}</strong>
                <span className="text-xs text-muted-foreground">{whenLabel(proposal)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <button
        className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-primary text-base font-semibold text-primary-foreground md:self-start md:px-6"
        onClick={() => setProposing(true)}
        type="button"
      >
        <svg
          aria-hidden="true"
          fill="none"
          height="18"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="2.4"
          viewBox="0 0 24 24"
          width="18"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        Proposer un souvenir
      </button>

      <section aria-labelledby="mon-acces" className="flex flex-col items-start gap-2 pt-4">
        <h2 className="m-0 text-[15px] font-semibold" id="mon-acces">
          Mon accès
        </h2>
        <p className="m-0 text-xs text-muted-foreground">
          Tu peux partir quand tu veux : ton compte et tes propositions en attente seront effacés.
        </p>
        <DeleteAccount
          buttonLabel="Supprimer mon accès"
          confirmLabel="Oui, supprimer mon accès"
          doneMessage="C'est fait : ton accès et ton compte sont effacés."
          explanation="Ton compte et tes propositions en attente seront effacés, et tu ne verras plus cette frise. Les souvenirs déjà acceptés restent sur la frise, sans ton nom. Pour revenir, il faudra une nouvelle invitation."
          heading="Supprimer ton accès ?"
        />
      </section>

      {proposing ? (
        <EntrySheet
          entry={null}
          onClose={() => setProposing(false)}
          onSaved={() => void queryClient.invalidateQueries({ queryKey: queryKeys.proposals })}
          propose={createProposal}
          tracks={tracks}
        />
      ) : null}
    </AppPage>
  );
}
